import React, { useCallback, useMemo } from 'react';
import { formatEventDateLong } from 'utils/dateTime';
import { useGlobalDateLocale } from 'context/DateLocaleContext';
import styled from 'styled-components';
import { useIsMobile } from 'WindowDimensionProvider';
import useWaitList from 'api/waitlist/useWaitList';
import getUpdatedSelectedDate from 'utils/data/getUpdatedSelectedData';
import getFirstAvailableDateFromSessions from 'utils/data/firstAvailableDate/getFirstAvailableDateFromSessions';
import useEventBookingSession from './hooks/useEventBookingSession';
import EventDetailsPage from './pages/EventDetailsPage/EventDetailsPage';
import EventTicketsPage from './pages/EventTicketsPage/EventTicketsPage';

export const EventTicketsRoute = () => {
  const isMobile = useIsMobile();
  const { locale } = useGlobalDateLocale();
  const { listing, date, setEventBookingSessionParams, daySessions } =
    useEventBookingSession();
  const { data: waitlist } = useWaitList(listing?.waitlistId, listing?.brandId);
  const firstAvailableScheduledDate =
    getFirstAvailableDateFromSessions(daySessions)?.startDateTime?.split(
      'T',
    )[0];
  const selectedDate = getUpdatedSelectedDate(
    date,
    firstAvailableScheduledDate,
    waitlist,
  );

  const deselectDate = useCallback(() => {
    setEventBookingSessionParams({
      date: undefined,
    });
  }, [setEventBookingSessionParams]);

  const dateFormatted = useMemo(
    () => (selectedDate ? formatEventDateLong(selectedDate, locale) : ''),
    [selectedDate, locale],
  );

  return (
    <>
      {isMobile && <EventDetailsPage suppressPageView />}
      <EventTicketsPage
        open
        heading={
          <BottomDrawerHeader
            title={listing?.title || ''}
            subTitle={dateFormatted}
          />
        }
        size="full"
        onClose={deselectDate}
      />
    </>
  );
};

interface HeaderProps {
  title: string;
  subTitle: string;
}

const BottomDrawerHeader = ({ title, subTitle }: HeaderProps) => {
  return (
    <HeaderWrapper>
      <BottomDrawerTitle className="event-mobile-title">
        {title}
      </BottomDrawerTitle>
      <BottomDrawerSubTitle className="event-mobile-subtitle">
        {subTitle}
      </BottomDrawerSubTitle>
    </HeaderWrapper>
  );
};

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const BottomDrawerTitle = styled.h1`
  margin: 0px;
  color: var(--way-colors-primaryTextColor);
  font-size: 24px;
  line-height: 26px;
`;

const BottomDrawerSubTitle = styled.p`
  margin: 0px;
  color: var(--way-colors-contrastColorShades-80);
  margin-bottom: 0px;
  font-size: 16px;
  line-height: 18px;
`;
